import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { moneyTight as money, zonedParts } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { pageToolbar, PAGE_INSET } from '../../tabPalette';   // the page toolbar's bubble + the one page gutter (report 76a2c198)

// ── Accounting fee board ────────────────────────────────────────────────────────────────────────
// Every accounting client down the side, every month across the top, plus the two year-end
// services. A cell says what was billed and — by its colour alone — whether the money came in and
// whether it came in on time (owner 2026-09-18).
//
// The colours ARE the feature; the numbers are just there to be checked against. So the palette is
// deliberately flat and the grid dense: the eye should land on red without reading anything.

type CellState = 'paid' | 'paid_late' | 'overdue' | 'open' | 'none';
interface Cell { amount: number; state: CellState; docNo: string; paidOn: string; due: string }
interface Row {
  clientId: string; company: string; client: string; taxId: string; group: string;
  months: Record<string, Cell>;
  bbfPrev: Cell | null; bsaPrev: Cell | null; bbf: Cell | null; bsa: Cell | null;
}

const MON = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MON_LABEL = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Paid is deliberately the quietest thing on the sheet — it is settled, it needs no attention.
// Late and overdue are the two that should pull the eye, and they are different reds because they
// need different actions: one is a client to chase now, the other is a habit to talk about.
const STYLE: Record<CellState, React.CSSProperties> = {
  paid:      { color: '#9AA3AD', background: 'transparent' },
  paid_late: { color: '#B4524A', background: '#FBEFEE' },
  overdue:   { color: '#fff', background: '#C0392B', fontWeight: 700 },
  open:      { color: 'var(--text-primary)', background: 'transparent' },
  none:      { color: 'transparent', background: 'transparent' },
};

function CellView({ c, t }: { c: Cell | null | undefined; t: any }) {
  if (!c || c.state === 'none' || !c.amount) return <span style={{ color: 'var(--text-tertiary)' }}>·</span>;
  const tip = [
    c.docNo,
    c.state === 'paid' || c.state === 'paid_late' ? t('fb_tip_paid', { on: c.paidOn }) : t('fb_tip_due', { on: c.due }),
    c.state === 'paid_late' ? t('fb_tip_late') : c.state === 'overdue' ? t('fb_tip_overdue') : '',
  ].filter(Boolean).join('\n');
  return <span title={tip} style={{ ...STYLE[c.state], borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{money(c.amount)}</span>;
}

export default function FeeBoardView() {
  const { t } = useTranslation();
  const thisYear = zonedParts().y;
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async (y: number) => {
    setBusy(true); setErr('');
    try { const d: any = await api.feeBoard(y); setRows(d.rows || []); }
    catch (e: any) { setErr(e.message); setRows([]); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { void load(year); }, [year, load]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r => `${r.company} ${r.client} ${r.taxId}`.toLowerCase().includes(s));
  }, [rows, q]);

  // Grouped exactly as the client file groups them (TRADING / PROPERTY / …) — the sheet the office
  // already reads is organised that way.
  const grouped = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of shown) { if (!m.has(r.group)) m.set(r.group, []); m.get(r.group)!.push(r); }
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [shown]);

  // 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"), and the
  // line under the headings is an inset shadow where it was a bottom border ("Make sure to include the horizontal gray
  // line for any pages that scroll"). The table collapses its borders, and a collapsed border stays with the table when
  // the sticky heading moves, so the headings lost their line as soon as the board scrolled; a shadow is the cell's own.
  // Then Active Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system
  // look like this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the
  // regular weight, where they were light grey in bold, on one 16px line over the darker --border-strong line. The shadow
  // lies over the cell's last pixel where a border would sit under it, so 10px above and 11 below, where both were 7,
  // make the band 37px as every list's is. The 11.5px stays.
  // The gap under the text is 5px tighter now, 11 → 6px (report c151d269: "Bring the heading text 5 pixels closer to
  // the grey line below. Do this for every page."), which leaves the band 32px — level with the bordered lists, whose
  // 10px bottom went to 5 over a 1px line.
  const th: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-surface)', padding: '10px 8px 6px', fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textAlign: 'right', whiteSpace: 'nowrap', boxShadow: 'inset 0 -1px 0 var(--border-strong)' };
  const td: React.CSSProperties = { padding: '4px 8px', fontSize: 11.5, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', fontVariantNumeric: 'tabular-nums' };
  const stick: React.CSSProperties = { position: 'sticky', left: 0, zIndex: 1, background: 'var(--bg-surface)', textAlign: 'left' };
  const yy = String(year).slice(2);
  const py = String(year - 1).slice(2);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-app)' }}>
      {/* The page's top bar — the name, the year, search and the colour legend — is a bubble, as every page toolbar is
          (Calem, report 76a2c198, 2nd opening — see pageToolbar): 10px corners and a --border-strong line all the way
          round where a --border line ran under it alone, PAGE_INSET in from the page's edges — the one page gutter the
          whole system stands in by, 15px (that report's 5th opening: "Move the left and right page spacing in from the
          edges to 15 px on every page of the SLF system"; see PAGE_INSET in tabPalette) — with a gap above and below
          it. The board below had nothing to line up with and ran edge to edge; it stands in by that same 15 now, so
          the board starts and ends where the bubble above it does. It is wider than the window, so that gutter is
          what you see before anything is scrolled; once the board is dragged sideways the columns pass through it,
          and the Company column still parks flush at the window's edge, since a sticky column measures from the
          scroll box's own edge and not from its padding. Leaving it there is deliberate — pinning it on the 15px
          line instead would leave a 15px sliver of the scrolled columns showing to its left. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', ...pageToolbar(), background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 15 }}>{t('mod_fee_board')}</b>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontWeight: 600 }}>
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('fb_search')}
          style={{ flex: '0 1 260px', padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none' }} />
        <span style={{ flex: 1 }} />
        {/* The legend is not decoration: a colour nobody can name is a colour nobody trusts. */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
          <span><span style={{ ...STYLE.paid, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)' }}>{money(0)}</span> {t('fb_k_paid')}</span>
          <span><span style={{ ...STYLE.paid_late, padding: '1px 6px', borderRadius: 4 }}>{money(0)}</span> {t('fb_k_late')}</span>
          <span><span style={{ ...STYLE.overdue, padding: '1px 6px', borderRadius: 4 }}>{money(0)}</span> {t('fb_k_overdue')}</span>
          <span><span style={{ ...STYLE.open, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)' }}>{money(0)}</span> {t('fb_k_open')}</span>
        </div>
      </div>

      {busy && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }} /></div>}
      {!!err && <div style={{ padding: 20, color: '#B42318' }}>{err}</div>}

      {!busy && !err && (
        <div style={{ flex: 1, overflow: 'auto', padding: `0 ${PAGE_INSET}px` }}>
          <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
            <thead><tr>
              {/* The corner heading sticks both ways, so it sits over both: over the other headings (th's 2) as the
                  board scrolls sideways and over the Company column (stick's 1) as it scrolls down. It had stick's 1
                  and was painted over (Calem, report 66c9d174 — see th). */}
              <th style={{ ...th, ...stick, zIndex: 3, minWidth: 190 }}>{t('fb_company')}</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 130 }}>{t('fb_client')}</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 110 }}>{t('fb_tax_id')}</th>
              <th style={th}>DEC{py}</th>
              <th style={th}>BBF{py}</th>
              <th style={th}>BSA{py}</th>
              {MON_LABEL.map(m => <th key={m} style={th}>{m}{yy}</th>)}
              <th style={th}>BBF{yy}</th>
              <th style={th}>BSA{yy}</th>
            </tr></thead>
            <tbody>
              {grouped.map(([grp, list]) => (
                <React.Fragment key={grp}>
                  <tr>
                    <td colSpan={18} style={{ ...td, ...stick, textAlign: 'left', fontWeight: 800, fontSize: 12, color: 'var(--accent)', background: 'var(--bg-active)' }}>
                      {grp} <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>{list.length}</span>
                    </td>
                  </tr>
                  {list.map(r => (
                    <tr key={r.clientId}>
                      <td style={{ ...td, ...stick, fontWeight: 600, color: 'var(--text-primary)' }}>{r.company || r.client || '—'}</td>
                      <td style={{ ...td, textAlign: 'left', color: 'var(--text-secondary)' }}>{r.client || '—'}</td>
                      <td style={{ ...td, textAlign: 'left', color: 'var(--text-tertiary)' }}>{r.taxId || '—'}</td>
                      <td style={td}><CellView c={r.months.dec_prev} t={t} /></td>
                      <td style={td}><CellView c={r.bbfPrev} t={t} /></td>
                      <td style={td}><CellView c={r.bsaPrev} t={t} /></td>
                      {MON.map(m => <td key={m} style={td}><CellView c={r.months[m]} t={t} /></td>)}
                      <td style={td}><CellView c={r.bbf} t={t} /></td>
                      <td style={td}><CellView c={r.bsa} t={t} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {!grouped.length && <tr><td colSpan={18} style={{ ...td, textAlign: 'center', padding: 44, color: 'var(--text-tertiary)' }}>{t('fb_empty')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
